import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { scrollToTop } from '../utils/scrollToTop';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Events', path: '/events' },
    { name: 'Members', path: '/members' },
    { name: 'Rules', path: '/rules' },
    { name: 'Auction', path: 'https://superplayerauction.com/auctions.php?actid=54532', external: true }
  ];

  const isActive = (path) => location.pathname === path;

  const handleNavClick = () => {
    scrollToTop();
    setIsOpen(false);
  };

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo Section */}
          <div className="flex items-center space-x-5">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-16 h-16 lg:w-18 lg:h-18 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center shadow-xl overflow-hidden relative p-[1px] lg:p-[2px]">
                <img 
                  src="/mylogo.png" 
                  alt="Kabir Nagar Khel Samiti Society Logo" 
                  className={`w-full h-full object-contain rounded-full transition-opacity duration-300 ${logoLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setLogoLoaded(true)}
                  onError={(e) => {
                    // Try JPG version if PNG fails
                    if (e.target.src.includes('.png')) {
                      e.target.src = '/mylogo.JPG';
                    } else {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
                <span 
                  className="text-white text-sm lg:text-md font-bold hidden items-center justify-center absolute inset-0"
                  style={{ display: 'none' }}
                >
                  KNKSS
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-h6 text-gray-900 leading-tight">
                  KNKSS
                </span>
                <span className="text-caption text-gray-600 font-medium">
                  Raipur
                </span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              item.external ? (
                <a
                  key={item.name}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-lg nav-text transition-all duration-200 text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={handleNavClick}
                  className={`px-4 py-2 rounded-lg nav-text transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>

          {/* Desktop Action Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              to="/register"
              onClick={handleNavClick}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2.5 rounded-lg btn-text
                       hover:from-blue-700 hover:to-blue-800 transition-all duration-200 
                       shadow-lg hover:shadow-xl hover:scale-105"
            >
              Register
            </Link>
            <Link
              to="/admin"
              onClick={handleNavClick}
              className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-6 py-2.5 rounded-lg btn-text
                       hover:from-gray-800 hover:to-gray-900 transition-all duration-200 
                       shadow-lg hover:shadow-xl hover:scale-105"
            >
              Admin
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 hover:text-gray-900 focus:outline-none focus:text-gray-900 p-2"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                item.external ? (
                  <a
                    key={item.name}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                    onClick={handleNavClick}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                      isActive(item.path)
                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                    }`}
                    onClick={handleNavClick}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>
            
            {/* Mobile Action Buttons */}
            <div className="px-2 pb-3 space-y-2">
              <Link
                to="/register"
                className="block w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 rounded-lg text-base font-semibold text-center
                         hover:from-blue-700 hover:to-blue-800 transition-all duration-200"
                onClick={handleNavClick}
              >
                Register
              </Link>
              <Link
                to="/admin"
                className="block w-full bg-gradient-to-r from-gray-700 to-gray-800 text-white px-4 py-3 rounded-lg text-base font-semibold text-center
                         hover:from-gray-800 hover:to-gray-900 transition-all duration-200"
                onClick={handleNavClick}
              >
                Admin
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;